import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import Slider from '@cartwhl/ui/atoms/Slider';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import ChevronCompactRightIcon from '@cartwhl/ui/icons/ChevronCompactRightIcon';
import EyeIcon from '@cartwhl/ui/icons/EyeIcon';
import ThreeJSSettingsIcon from '@cartwhl/ui/icons/ThreeJSSettingsIcon';
import ToggleButton from '@cartwhl/ui/input/ToggleButton';

import type {
  BoneColorPreset,
  ToolbarMenuMode,
  ViewerVisibility,
} from '../../../cartographerStore';
import {
  BonesIcon,
  CameraFollowMotionIcon,
  ChainsIcon,
  ExcludedBoneIcon,
  MeshesIcon,
  NamesIcon,
  PropertiesIcon,
  RolesIcon,
  UnassignedBoneIcon,
} from '../../../presentations/icons';
import {
  COLLAPSE_CONTROL_BUTTON_CLASS,
  COLLAPSE_CONTROL_ICON_CLASS,
} from '../collapseControlStyle';
import ResetToDefaultButton from '../../../presentations/ResetToDefaultButton';

type ViewerVisibilityKey = Extract<keyof ViewerVisibility, string>;

type VisibilityItem = {
  key: ViewerVisibilityKey;
  label: string;
  icon: ReactNode;
  options?: VisibilityOptionItem[];
};

type VisibilityOptionItem = {
  key: ViewerVisibilityKey;
  label: string;
  icon: ReactNode;
};

type SettingsItem = {
  key: ViewerVisibilityKey;
  label: string;
};

type BoneColorPresetOption = {
  key: BoneColorPreset;
  label: string;
  swatchColor: string;
};

const VISIBILITY_ICON_CLASS = 'block h-[18px] w-[18px]';

const VISIBILITY_ITEMS: VisibilityItem[] = [
  {
    key: 'showMesh',
    label: 'Meshes',
    icon: (
      <VisibilityIconFrame>
        <MeshesIcon className={VISIBILITY_ICON_CLASS} />
      </VisibilityIconFrame>
    ),
  },
  {
    key: 'showBones',
    label: 'Bones',
    icon: (
      <VisibilityIconFrame>
        <BonesIcon className={VISIBILITY_ICON_CLASS} />
      </VisibilityIconFrame>
    ),
    options: [
      {
        key: 'showExcludedBones',
        label: 'Excluded bones',
        icon: (
          <VisibilityIconFrame>
            <ExcludedBoneIcon className={VISIBILITY_ICON_CLASS} />
          </VisibilityIconFrame>
        ),
      },
      {
        key: 'showUnassignedBones',
        label: 'Unassigned bones',
        icon: (
          <VisibilityIconFrame>
            <UnassignedBoneIcon className={VISIBILITY_ICON_CLASS} />
          </VisibilityIconFrame>
        ),
      },
      {
        key: 'showBoneNames',
        label: 'Bone labels',
        icon: (
          <VisibilityIconFrame>
            <NamesIcon className={VISIBILITY_ICON_CLASS} />
          </VisibilityIconFrame>
        ),
      },
    ],
  },
  {
    key: 'showRoles',
    label: 'Roles',
    icon: (
      <VisibilityIconFrame>
        <RolesIcon className={VISIBILITY_ICON_CLASS} />
      </VisibilityIconFrame>
    ),
  },
  {
    key: 'showChains',
    label: 'Chains',
    icon: (
      <VisibilityIconFrame>
        <ChainsIcon className={VISIBILITY_ICON_CLASS} />
      </VisibilityIconFrame>
    ),
  },
  {
    key: 'showProperties',
    label: 'Properties',
    icon: (
      <VisibilityIconFrame>
        <PropertiesIcon className={VISIBILITY_ICON_CLASS} />
      </VisibilityIconFrame>
    ),
  },
];

const SETTINGS_ITEMS: SettingsItem[] = [
  { key: 'showGrid', label: 'Grid' },
  { key: 'showShadow', label: 'Shadows' },
  { key: 'useDefaultShader', label: 'Default Shader' },
  { key: 'showSelectedBoneAxes', label: 'Selected Bone Axis' },
];
const BONE_COLOR_PRESET_OPTIONS: BoneColorPresetOption[] = [
  { key: 'white', label: 'White', swatchColor: '#ffffff' },
  { key: 'grey', label: 'Grey', swatchColor: '#808080' },
  { key: 'charcoal', label: 'Charcoal', swatchColor: '#555555' },
  { key: 'black', label: 'Black', swatchColor: '#000000' },
];

const TOOLBAR_ROW_HEIGHT_PX = 30;
const TOOLBAR_GAP_PX = 4;
const TOOLBAR_PANEL_PADDING_PX = 6;
const SETTINGS_ROW_WIDTH_PX = 210;
const SETTINGS_SCALE_MIN = 0.1;
const SETTINGS_SCALE_MAX = 10;
const SETTINGS_SCALE_DEFAULT = 1;
const SETTINGS_SCALE_RESET_EPSILON = 0.005;
const SETTINGS_SCALE_LOG_MIN = -1;
const SETTINGS_SCALE_LOG_MAX = 1;
const SETTINGS_SCALE_STEP = 0.01;
const GROUP_BACKGROUND_SYNC_MS = 200;
const SETTINGS_SLIDER_CLASS =
  'mt-1 [&>*:first-child]:h-[6px] [&>[role=slider]]:h-[18px] [&>[role=slider]]:w-[18px] [&>[role=slider]]:border-[3px]';
const SETTINGS_SLIDER_VALUE_CLASS =
  'bg-white/95 dark:bg-gray-90/95 text-primary tabular-nums inline-flex h-6 w-[58px] items-center justify-center rounded-xl';

function clampSettingsScale(scale: number): number {
  return Math.min(SETTINGS_SCALE_MAX, Math.max(SETTINGS_SCALE_MIN, scale));
}

interface VisibilityToggleWithOptionsProps {
  item: VisibilityItem;
  visibility: ViewerVisibility;
  onToggle: (key: ViewerVisibilityKey) => void;
}

function VisibilityToggleWithOptions({
  item,
  visibility,
  onToggle,
}: VisibilityToggleWithOptionsProps) {
  const active = visibility[item.key];
  const actionLabel = `${active ? 'Hide' : 'Show'} ${item.label}`;
  const options = item.options ?? [];
  const optionsVisible = active && options.length > 0;
  const [groupBackgroundVisible, setGroupBackgroundVisible] =
    useState(optionsVisible);
  const hideBackgroundTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (hideBackgroundTimeoutRef.current !== null) {
      clearTimeout(hideBackgroundTimeoutRef.current);
      hideBackgroundTimeoutRef.current = null;
    }

    if (optionsVisible) {
      setGroupBackgroundVisible(true);
      return;
    }

    hideBackgroundTimeoutRef.current = window.setTimeout(() => {
      hideBackgroundTimeoutRef.current = null;
      setGroupBackgroundVisible(false);
    }, GROUP_BACKGROUND_SYNC_MS);
  }, [optionsVisible]);

  useEffect(
    () => () => {
      if (hideBackgroundTimeoutRef.current !== null) {
        clearTimeout(hideBackgroundTimeoutRef.current);
      }
    },
    [],
  );

  return (
    <div
      className={`relative flex items-center rounded-lg ${groupBackgroundVisible ? 'gap-1 px-1.5' : 'gap-0'}`}
    >
      {options.length > 0 && groupBackgroundVisible && (
        <div
          className="pointer-events-none absolute inset-x-0 -inset-y-1 rounded-lg bg-gray-60/40 shadow-inner dark:bg-black/40"
        />
      )}

      <div
        className={`relative z-10 flex items-center ${groupBackgroundVisible ? 'gap-1' : 'gap-0'}`}
      >
        <Tooltip text={actionLabel} side="bottom" inModal>
          <ToggleButton
            active={active}
            onClick={() => onToggle(item.key)}
            aria-label={actionLabel}
            icon={item.icon}
          />
        </Tooltip>

        {options.length > 0 && (
          <div
            className={`grid overflow-hidden transition-all duration-200 ease-out ${optionsVisible ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
            style={{ gridTemplateColumns: optionsVisible ? '1fr' : '0fr' }}
            aria-hidden={!optionsVisible}
          >
            <div className="min-w-0 overflow-hidden">
              <div className="flex h-[30px] items-center gap-1 rounded-lg pl-1 pr-0">
                {options.map((option) => {
                  const optionActive = visibility[option.key];
                  const optionActionLabel = `${optionActive ? 'Hide' : 'Show'} ${option.label}`;
                  return (
                    <Tooltip
                      key={option.key}
                      text={optionActionLabel}
                      side="bottom"
                      inModal
                    >
                      <ToggleButton
                        active={optionActive}
                        onClick={() => onToggle(option.key)}
                        aria-label={optionActionLabel}
                        icon={option.icon}
                      />
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function scaleToSliderValue(scale: number): number {
  return Math.log10(clampSettingsScale(scale));
}

function sliderToScaleValue(sliderValue: number): number {
  const clampedLog = Math.min(
    SETTINGS_SCALE_LOG_MAX,
    Math.max(SETTINGS_SCALE_LOG_MIN, sliderValue),
  );
  return Number((10 ** clampedLog).toFixed(1));
}

interface ViewerToolbarProps {
  visibility: ViewerVisibility;
  menuMode: ToolbarMenuMode;
  boneColorPreset: BoneColorPreset;
  boneRadiusScale: number;
  chainThicknessScale: number;
  manipulatorScale: number;
  cameraFollowMotion: boolean;
  showCameraFollowMotionButton?: boolean;
  showVisibilityLabel?: boolean;
  showSettingsLabel?: boolean;
  onToggle: (key: ViewerVisibilityKey) => void;
  onBoneColorPresetChange: (preset: BoneColorPreset) => void;
  onBoneRadiusScaleChange: (value: number) => void;
  onChainThicknessScaleChange: (value: number) => void;
  onManipulatorScaleChange: (value: number) => void;
  onCameraFollowMotionChange: (enabled: boolean) => void;
  onMenuModeChange: (mode: ToolbarMenuMode) => void;
}

interface VisibilitySectionProps {
  items: VisibilityItem[];
  visibility: ViewerVisibility;
  isOpen: boolean;
  onToggle: (key: ViewerVisibilityKey) => void;
  onCollapse: () => void;
}

interface ToolbarTriggerProps {
  label: string;
  icon: ReactNode;
  active: boolean;
  showLabel?: boolean;
  onClick: () => void;
  className?: string;
  tooltipText?: string;
  ariaLabel?: string;
}

function VisibilityIconFrame({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-5 w-5 items-center justify-center">{children}</span>
  );
}

function ToolbarTrigger({
  label,
  icon,
  active,
  showLabel = true,
  onClick,
  className = '',
  tooltipText,
  ariaLabel,
}: ToolbarTriggerProps) {
  const resolvedTooltipText = tooltipText ?? (active ? `Close ${label}` : `Open ${label}`);

  return (
    <Tooltip text={resolvedTooltipText} side="bottom" inModal>
      <button
        type="button"
        onClick={onClick}
        className={`focus-visible inline-flex h-[30px] items-center rounded-lg transition-all duration-150 ${showLabel ? 'gap-2 px-2.5' : 'justify-center px-2'} ${active ? 'dark:bg-gray-90 bg-white text-gray-90 dark:text-gray-15' : 'bg-gray-15/25 text-gray-90 dark:bg-gray-90/25 dark:text-gray-15 hover:bg-gray-15 dark:hover:bg-gray-90'} ${className}`}
        aria-pressed={active}
        aria-label={ariaLabel ?? resolvedTooltipText}
      >
        <span className="flex h-5 w-5 items-center justify-center">{icon}</span>
        {showLabel && (
          <Text as="span" variant="studio/body/medium/xs">
            {label}
          </Text>
        )}
      </button>
    </Tooltip>
  );
}

function VisibilitySection({
  items,
  visibility,
  isOpen,
  onToggle,
  onCollapse,
}: VisibilitySectionProps) {
  return (
    <div
      className={`flex items-center gap-1 transition-all duration-200 ease-out ${isOpen ? 'pointer-events-auto max-w-[640px] overflow-visible opacity-100 translate-x-0' : 'pointer-events-none max-w-0 overflow-hidden opacity-0 -translate-x-2'}`}
    >
      <Tooltip text="Collapse visibility menu" side="bottom" inModal>
        <button
          type="button"
          onClick={onCollapse}
          className={COLLAPSE_CONTROL_BUTTON_CLASS}
          aria-label="Collapse visibility menu"
        >
          <ChevronCompactRightIcon className={COLLAPSE_CONTROL_ICON_CLASS} />
        </button>
      </Tooltip>

      {items.map((item) => (
        <VisibilityToggleWithOptions
          key={item.key}
          item={item}
          visibility={visibility}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

interface SettingsPillToggleProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

function SettingsPillToggle({ active, label, onClick }: SettingsPillToggleProps) {
  const actionLabel = `${active ? 'Hide' : 'Show'} ${label}`;
  return (
    <Tooltip text={actionLabel} side="bottom" inModal>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex h-4 w-8 items-center rounded-full transition-colors ${active ? 'bg-blue-50' : 'bg-gray-30 dark:bg-gray-60'}`}
        aria-label={`${active ? 'Disable' : 'Enable'} ${label}`}
        aria-pressed={active}
      >
        <span
          className={`block h-3 w-3 rounded-full bg-white shadow transition-transform ${active ? 'translate-x-4' : 'translate-x-0.5'}`}
        />
      </button>
    </Tooltip>
  );
}

interface SettingsToggleRowProps {
  label: string;
  active: boolean;
  onToggle: () => void;
}

function SettingsToggleRow({ label, active, onToggle }: SettingsToggleRowProps) {
  return (
    <div
      className="dark:bg-gray-90/20 bg-white/20 flex h-[30px] items-center justify-between rounded-lg px-2"
      style={{ width: SETTINGS_ROW_WIDTH_PX }}
    >
      <Text variant="studio/body/medium/xs" className="text-primary">
        {label}
      </Text>
      <SettingsPillToggle active={active} label={label} onClick={onToggle} />
    </div>
  );
}

interface SettingsSliderRowProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
}

function SettingsSliderRow({ label, value, onChange }: SettingsSliderRowProps) {
  const safeValue = clampSettingsScale(value);
  const isNonDefaultValue =
    Math.abs(safeValue - SETTINGS_SCALE_DEFAULT) > SETTINGS_SCALE_RESET_EPSILON;
  const resetLabel = `Reset ${label} to 1x`;
  return (
    <div
      className="dark:bg-gray-90/15 bg-white/15 rounded-lg px-2 py-1.5"
      style={{ width: SETTINGS_ROW_WIDTH_PX }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <Text
            variant="studio/body/medium/xs"
            className="text-secondary whitespace-nowrap"
          >
            {label}
          </Text>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <ResetToDefaultButton
            label={label}
            tooltipText={resetLabel}
            side="bottom"
            inModal
            isDefault={!isNonDefaultValue}
            onReset={() => onChange(SETTINGS_SCALE_DEFAULT)}
          />
          <Text
            variant="studio/body/medium/xs"
            className={SETTINGS_SLIDER_VALUE_CLASS}
          >
            {safeValue.toFixed(1)}x
          </Text>
        </div>
      </div>
      <Slider
        className={SETTINGS_SLIDER_CLASS}
        trackColor="duration"
        rangeColor="blue"
        size="lg"
        min={SETTINGS_SCALE_LOG_MIN}
        max={SETTINGS_SCALE_LOG_MAX}
        step={SETTINGS_SCALE_STEP}
        currentValue={scaleToSliderValue(safeValue)}
        onChange={(nextLogValue) => onChange(sliderToScaleValue(nextLogValue))}
      />
    </div>
  );
}

interface SettingsBoneColorPresetButtonProps {
  preset: BoneColorPresetOption;
  active: boolean;
  onSelect: (preset: BoneColorPreset) => void;
}

function SettingsBoneColorPresetButton({
  preset,
  active,
  onSelect,
}: SettingsBoneColorPresetButtonProps) {
  const actionLabel = `Set bone color to ${preset.label}`;
  return (
    <Tooltip text={actionLabel} side="bottom" inModal>
      <button
        type="button"
        onClick={() => onSelect(preset.key)}
        className={`h-6 flex-1 rounded-full border transition-all duration-150 ${active ? 'border-gray-30/80 dark:border-gray-60/80 outline outline-2 outline-offset-1 outline-blue-50' : 'border-gray-30/80 dark:border-gray-60/80 hover:opacity-85'}`}
        style={{ backgroundColor: preset.swatchColor }}
        aria-label={actionLabel}
        aria-pressed={active}
      />
    </Tooltip>
  );
}

interface SettingsBoneColorRowProps {
  selectedPreset: BoneColorPreset;
  onPresetChange: (preset: BoneColorPreset) => void;
}

function SettingsBoneColorRow({
  selectedPreset,
  onPresetChange,
}: SettingsBoneColorRowProps) {
  return (
    <div
      className="dark:bg-gray-90/15 bg-white/15 rounded-lg px-2 py-1.5"
      style={{ width: SETTINGS_ROW_WIDTH_PX }}
    >
      <Text variant="studio/body/medium/xs" className="text-secondary">
        Bone color
      </Text>
      <div className="mt-1 flex items-center gap-1">
        {BONE_COLOR_PRESET_OPTIONS.map((preset) => (
          <SettingsBoneColorPresetButton
            key={preset.key}
            preset={preset}
            active={selectedPreset === preset.key}
            onSelect={onPresetChange}
          />
        ))}
      </div>
    </div>
  );
}

interface SettingsWidgetProps {
  items: SettingsItem[];
  visibility: ViewerVisibility;
  boneColorPreset: BoneColorPreset;
  boneRadiusScale: number;
  chainThicknessScale: number;
  manipulatorScale: number;
  showSettingsLabel: boolean;
  isOpen: boolean;
  outsideClickBoundaryRef?: RefObject<HTMLDivElement | null>;
  onToggle: (key: ViewerVisibilityKey) => void;
  onBoneColorPresetChange: (preset: BoneColorPreset) => void;
  onBoneRadiusScaleChange: (value: number) => void;
  onChainThicknessScaleChange: (value: number) => void;
  onManipulatorScaleChange: (value: number) => void;
  onOpen: () => void;
  onClose: () => void;
}

function SettingsWidget({
  items,
  visibility,
  boneColorPreset,
  boneRadiusScale,
  chainThicknessScale,
  manipulatorScale,
  showSettingsLabel,
  isOpen,
  outsideClickBoundaryRef,
  onToggle,
  onBoneColorPresetChange,
  onBoneRadiusScaleChange,
  onChainThicknessScaleChange,
  onManipulatorScaleChange,
  onOpen,
  onClose,
}: SettingsWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rowsRef = useRef<HTMLDivElement>(null);
  const [rowsContentHeightPx, setRowsContentHeightPx] = useState(0);
  const rowsCount = items.length + 4;

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (outsideClickBoundaryRef?.current?.contains(target)) return;
      if (containerRef.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isOpen, onClose, outsideClickBoundaryRef]);

  useLayoutEffect(() => {
    const element = rowsRef.current;
    if (!element) return;

    const measure = () => {
      const nextHeight = Math.ceil(element.getBoundingClientRect().height);
      setRowsContentHeightPx((prevHeight) =>
        prevHeight === nextHeight ? prevHeight : nextHeight,
      );
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => measure());
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const collapsedHeightPx =
    TOOLBAR_ROW_HEIGHT_PX + TOOLBAR_PANEL_PADDING_PX * 2;
  const rowsHeightPx = rowsCount === 0 ? 0 : rowsContentHeightPx;
  const expandedHeightPx =
    collapsedHeightPx + (rowsCount > 0 ? TOOLBAR_GAP_PX + rowsHeightPx : 0);

  return (
    <div ref={containerRef} className="relative">
      <div
        className="dark:bg-gray-90/40 bg-gray-15/45 rounded-2xl p-1.5 backdrop-blur-md overflow-hidden transition-[max-height] duration-200 ease-out"
        style={{
          maxHeight: isOpen ? expandedHeightPx : collapsedHeightPx,
        }}
      >
        <div className="flex flex-col gap-0">
          <ToolbarTrigger
            label="Settings"
            icon={<ThreeJSSettingsIcon className="h-[18px] w-[18px]" />}
            active={isOpen}
            showLabel={showSettingsLabel}
            onClick={isOpen ? onClose : onOpen}
            className={showSettingsLabel ? 'justify-start' : ''}
          />
          <div
            className={`min-h-0 min-w-0 overflow-hidden transition-[max-height,max-width,opacity,margin-top] duration-200 ease-out ${isOpen ? '' : 'pointer-events-none'}`}
            style={{
              marginTop: isOpen && rowsCount > 0 ? TOOLBAR_GAP_PX : 0,
              maxHeight: isOpen ? rowsHeightPx : 0,
              maxWidth: isOpen ? SETTINGS_ROW_WIDTH_PX : 0,
              opacity: isOpen ? 1 : 0,
              visibility: isOpen ? 'visible' : 'hidden',
            }}
          >
            <div ref={rowsRef} className="flex flex-col gap-1">
              {items.map(({ key, label }) => (
                <SettingsToggleRow
                  key={key}
                  label={label}
                  active={visibility[key]}
                  onToggle={() => onToggle(key)}
                />
              ))}
              <SettingsBoneColorRow
                selectedPreset={boneColorPreset}
                onPresetChange={onBoneColorPresetChange}
              />
              <SettingsSliderRow
                label="Bone size"
                value={boneRadiusScale}
                onChange={onBoneRadiusScaleChange}
              />
              <SettingsSliderRow
                label="Chain thickness"
                value={chainThicknessScale}
                onChange={onChainThicknessScaleChange}
              />
              <SettingsSliderRow
                label="Manipulator size"
                value={manipulatorScale}
                onChange={onManipulatorScaleChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface CameraFollowMotionWidgetProps {
  active: boolean;
  onToggle: () => void;
}

function CameraFollowMotionWidget({
  active,
  onToggle,
}: CameraFollowMotionWidgetProps) {
  return (
    <div className="dark:bg-gray-90/40 bg-gray-15/45 rounded-2xl p-1.5 backdrop-blur-md">
      <div className="flex items-center gap-1">
        <ToolbarTrigger
          label="Camera Follow Motion"
          icon={<CameraFollowMotionIcon className="h-[18px] w-[18px]" />}
          active={active}
          showLabel={false}
          onClick={onToggle}
          tooltipText={`${active ? 'Disable' : 'Enable'} Camera Follow Motion`}
          ariaLabel={`${active ? 'Disable' : 'Enable'} Camera Follow Motion`}
        />
      </div>
    </div>
  );
}

export default function ViewerToolbar({
  visibility,
  menuMode,
  boneColorPreset,
  boneRadiusScale,
  chainThicknessScale,
  manipulatorScale,
  cameraFollowMotion,
  showCameraFollowMotionButton = false,
  showVisibilityLabel = true,
  showSettingsLabel = true,
  onToggle,
  onBoneColorPresetChange,
  onBoneRadiusScaleChange,
  onChainThicknessScaleChange,
  onManipulatorScaleChange,
  onCameraFollowMotionChange,
  onMenuModeChange,
}: ViewerToolbarProps) {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const visibilityOpen = menuMode === 'visibility';
  const settingsOpen = menuMode === 'settings';
  const shouldShowVisibilityLabel = showVisibilityLabel && !settingsOpen;
  const shouldShowSettingsLabel = showSettingsLabel && !visibilityOpen;

  return (
    <div ref={toolbarRef} className="pointer-events-auto flex items-start gap-1">
      <div className="dark:bg-gray-90/40 bg-gray-15/45 rounded-2xl p-1.5 backdrop-blur-md">
        <div className="flex items-center gap-1">
          <VisibilitySection
            items={VISIBILITY_ITEMS}
            visibility={visibility}
            isOpen={visibilityOpen}
            onToggle={onToggle}
            onCollapse={() => onMenuModeChange('none')}
          />
          <ToolbarTrigger
            label="Visibility"
            icon={<EyeIcon className="h-3.5 w-3.5" />}
            active={visibilityOpen}
            showLabel={shouldShowVisibilityLabel}
            onClick={() =>
              onMenuModeChange(visibilityOpen ? 'none' : 'visibility')
            }
          />
        </div>
      </div>

      <SettingsWidget
        items={SETTINGS_ITEMS}
        visibility={visibility}
        boneColorPreset={boneColorPreset}
        boneRadiusScale={boneRadiusScale}
        chainThicknessScale={chainThicknessScale}
        manipulatorScale={manipulatorScale}
        showSettingsLabel={shouldShowSettingsLabel}
        isOpen={settingsOpen}
        outsideClickBoundaryRef={toolbarRef}
        onToggle={onToggle}
        onBoneColorPresetChange={onBoneColorPresetChange}
        onBoneRadiusScaleChange={onBoneRadiusScaleChange}
        onChainThicknessScaleChange={onChainThicknessScaleChange}
        onManipulatorScaleChange={onManipulatorScaleChange}
        onOpen={() => onMenuModeChange('settings')}
        onClose={() => onMenuModeChange('none')}
      />
      {showCameraFollowMotionButton && (
        <CameraFollowMotionWidget
          active={cameraFollowMotion}
          onToggle={() => onCameraFollowMotionChange(!cameraFollowMotion)}
        />
      )}
    </div>
  );
}
